import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { Divider, InfoTooltip } from "@impero-com/ui";
import memoizeOne from "memoize-one";

import {
    SubsequentStepIndex,
    Quarter,
    SchedulingOccurrenceChangeForDueDateOffsetForMonthOffsetPeriod as SchedulingOccurrenceChange,
    UpdatableRecurrenceRuleWithOffsetAndFinancialYearForQuarterAndDueDateOffsetForMonthOffsetPeriodAndUpdatableDueDateOffsetForUpdatableMonthOffsetPeriod as UpdatableQuarterlyRecurrenceRule,
    FinancialYearView,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { isNotUndefined } from "utils/typePredicates";

import type { PeriodType } from "../common/multiDefaultDueDateOffsetInput";
import { MultiFinancialYearCheckbox } from "../common/multiFinancialYearCheckbox";
import {
    GenericOccurrencesArray,
    QuarterlyRecurrenceRuleWithArrays,
} from "../common/rulesWithArrays";

import { MultiQuarterlyCheckboxes } from "./multiQuarterlyCheckboxes";

const toQuarterlyArrayRecords = memoizeOne(
    (
        occurrences: GenericOccurrencesArray<Quarter>
    ): Record<Quarter, boolean[]> => {
        return {
            First: occurrences.First.map(isNotUndefined),
            Second: occurrences.Second.map(isNotUndefined),
            Third: occurrences.Third.map(isNotUndefined),
            Fourth: occurrences.Fourth.map(isNotUndefined),
        };
    }
);

abstract class BaseQuarterlyRecurrenceRuleUpdaterForm<
    P,
> extends React.PureComponent<P> {
    render = () => {
        return (
            <>
                <Divider />
                {this.buildDefaultDueDateOffsetInput()}
                <Divider />
                <h1>
                    {t("CreateControl_Planning_Title")}&nbsp;
                    <InfoTooltip
                        text={t("Control_ModifySchedule_PlanningInfo")}
                    />
                </h1>
                {this.buildResponsibleAllocatedTimeInputs()}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep0")}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep1")}
                {this.buildFinancialYearCheckbox()}
                <Divider />
                {this.buildQuarterly()}
                {this.buildCustomDueDateDialog()}
                {this.buildRemindersWidget()}
            </>
        );
    };

    abstract buildDefaultDueDateOffsetInput(): React.ReactNode;

    abstract buildResponsibleAllocatedTimeInputs(): React.ReactNode;

    abstract buildReviewerAllocatedTimeInputs(
        subsequentStepIndex: SubsequentStepIndex
    ): React.ReactNode;

    abstract buildFinancialYearCheckbox(): React.ReactNode;

    abstract buildQuarterly(): React.ReactNode;

    abstract buildCustomDueDateDialog(): React.ReactNode;

    abstract buildRemindersWidget(): React.ReactNode;
}

interface BasePartialQuarterlyRecurrenceRuleProps {
    financialYear: FinancialYearView | null;
    currentRule: QuarterlyRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT" | "READ_ONLY",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildCustomDueDateDialog: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT" | "READ_ONLY") => React.ReactNode;
}

type PartialQuarterlyRecurrenceRuleUpdaterProps =
    BasePartialQuarterlyRecurrenceRuleProps &
        UpdatableQuarterlyRecurrenceRule & {
            onChangeFinancialYear: (financialYearId: number | null) => void;
            onChangeQuarterlyOccurrence: (
                interval: Quarter,
                value: SchedulingOccurrenceChange | undefined
            ) => void;
        };

class PartialQuarterlyRecurrenceRuleUpdater extends BaseQuarterlyRecurrenceRuleUpdaterForm<PartialQuarterlyRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "Quarter");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId?.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox
                    type="EDIT"
                    values={values}
                    onChange={this.handleFinancialYearChange}
                />
            );
        } else {
            return <></>;
        }
    };

    private handleFinancialYearChange = (checked: boolean) => {
        const { financialYear, onChangeFinancialYear } = this.props;
        let id = null;
        if (checked && financialYear) {
            id = financialYear.id;
        }
        onChangeFinancialYear(id);
    };

    buildQuarterly = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toQuarterlyArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiQuarterlyCheckboxes
                    type="EDIT"
                    quarters={values}
                    onChange={this.onChangeQuarterly}
                />
            </BaseFormDisplay>
        );
    };

    onChangeQuarterly = (key: Quarter, value: boolean) => {
        const { onChangeQuarterlyOccurrence } = this.props;
        onChangeQuarterlyOccurrence(
            key,
            value ? "ActivateOnlyDeactivated" : undefined
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("EDIT");

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface FullQuarterlyRecurrenceRuleUpdaterProps {
    financialYear: FinancialYearView | null;
    currentRule: QuarterlyRecurrenceRuleWithArrays;
    buildDefaultDueDateOffsetInput: (
        readOnly: "EDIT",
        period: PeriodType
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (readOnly: "EDIT") => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildCustomDueDateDialog: (readOnly: "EDIT") => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT") => React.ReactNode;
    onChangeFinancialYear: (financialYearId: number | null) => void;
    onChangeQuarterlyOccurrence: (
        interval: Quarter,
        value: SchedulingOccurrenceChange | undefined
    ) => void;
}

class FullQuarterlyRecurrenceRuleUpdater extends BaseQuarterlyRecurrenceRuleUpdaterForm<FullQuarterlyRecurrenceRuleUpdaterProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("EDIT", "Quarter");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId?.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox
                    type="EDIT"
                    values={values}
                    onChange={this.handleFinancialYearChange}
                />
            );
        } else {
            return <></>;
        }
    };

    private handleFinancialYearChange = (checked: boolean) => {
        const { financialYear, onChangeFinancialYear } = this.props;
        let id = null;
        if (checked && financialYear) {
            id = financialYear.id;
        }
        onChangeFinancialYear(id);
    };

    buildQuarterly = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toQuarterlyArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiQuarterlyCheckboxes
                    type="EDIT"
                    quarters={values}
                    onChange={this.onChangeQuarterly}
                />
            </BaseFormDisplay>
        );
    };

    onChangeQuarterly = (key: Quarter, value: boolean) => {
        const { onChangeQuarterlyOccurrence } = this.props;
        onChangeQuarterlyOccurrence(
            key,
            value ? "ActivateOnlyDeactivated" : undefined
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("EDIT");

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

class PartialQuarterlyRecurrenceRuleViewer extends BaseQuarterlyRecurrenceRuleUpdaterForm<PartialQuarterlyRecurrenceRuleViewerProps> {
    buildDefaultDueDateOffsetInput = () =>
        this.props.buildDefaultDueDateOffsetInput("READ_ONLY", "Quarter");

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("READ_ONLY");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "READ_ONLY",
            subsequentStepIndex
        );

    buildFinancialYearCheckbox = () => {
        const { financialYear, currentRule } = this.props;

        if (financialYear) {
            const values = currentRule.financialYearId.map((id) => !!id);
            return (
                <MultiFinancialYearCheckbox type="READ_ONLY" values={values} />
            );
        } else {
            return <></>;
        }
    };

    buildQuarterly = () => {
        const {
            currentRule: { occurrences },
        } = this.props;
        const values = toQuarterlyArrayRecords(occurrences);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Advanced")}>
                <MultiQuarterlyCheckboxes type="READ_ONLY" quarters={values} />
            </BaseFormDisplay>
        );
    };

    buildCustomDueDateDialog = () =>
        this.props.buildCustomDueDateDialog("READ_ONLY");

    buildRemindersWidget = () => this.props.buildRemindersWidget("READ_ONLY");
}

type PartialQuarterlyRecurrenceRuleViewerProps =
    BasePartialQuarterlyRecurrenceRuleProps;

export {
    FullQuarterlyRecurrenceRuleUpdater,
    PartialQuarterlyRecurrenceRuleUpdater,
    PartialQuarterlyRecurrenceRuleViewer,
};
